import React, { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { Button, FormControl, MenuItem, Select, SelectChangeEvent, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';

import { deleteVendorAgreement } from '~/services/BusinessTraveller/BusinessTravellerService';

import { titleCase } from '~/utils/stringUtils';

import { VendorAgreementItemType } from '../pages/BusinessDetailsPage/Tabs/FrequentFlyerTab';

const OSI_SSR_MAX_LENGTH = 100;
const SSR_CODE_LENGTH = 4;

interface Props {
  businessId: string;
  item: VendorAgreementItemType;
  index: number;
  frequentFlyerItems: Array<VendorAgreementItemType>;
  setFrequentFlyerItems: (value: SetStateAction<Array<VendorAgreementItemType>>) => void;
  setHasSaved: Dispatch<SetStateAction<boolean>>;
}

export default function CorporateAirAgreementItem({
  businessId,
  item,
  index,
  frequentFlyerItems,
  setFrequentFlyerItems,
  setHasSaved,
}: Props) {
  function deleteRow(indexToRemove: number, businessId: string) {
    const frequentFlyerCopy = [...frequentFlyerItems];

    if (frequentFlyerCopy[indexToRemove].id) {
      deleteVendorAgreement(frequentFlyerCopy[indexToRemove].id, businessId);
    }

    frequentFlyerCopy.splice(indexToRemove, 1); // Remove the selected row
    setFrequentFlyerItems(frequentFlyerCopy);
    setHasSaved(false);
  }

  function onSelectVerified(e: SelectChangeEvent<boolean>, index: number) {
    const { name, value } = e.target;

    const frequentFlyerCopy = [...frequentFlyerItems];
    frequentFlyerCopy[index][name] = value === 'true';

    setFrequentFlyerItems(frequentFlyerCopy);
    setHasSaved(false);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) {
    const { name, value } = e.target;

    const frequentFlyerCopy = [...frequentFlyerItems];
    frequentFlyerCopy[index][name] = value;

    setFrequentFlyerItems(frequentFlyerCopy);
    setHasSaved(false);
  }

  return (
    <Grid container gap={2} key={item.id} spacing={2}>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid>
          <Typography fontSize={32} fontWeight="bold">
            Corporate Air Agreement: {titleCase(item.vendorName)}
          </Typography>
        </Grid>
        <Grid>
          <Button color="error" variant="outlined" onClick={() => deleteRow(index, businessId)}>
            Delete
          </Button>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>Airline:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField name="vendorName" value={`${titleCase(item.vendorName)} (${item.vendorCode})`} disabled />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>Corporate code:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField
              name="corporateCode"
              value={item.corporateCode}
              onChange={(event) => handleInputChange(event, index)}
              helperText="Corporate code issued by the airline, e.g. ABC12"
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>Identity code:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField
              name="identityCode"
              value={item.identityCode}
              onChange={(event) => handleInputChange(event, index)}
              helperText="Identity code issued by the airline, e.g. 123456. Same as the corporate code if the airline does not give one"
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>OSI:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField
              name="osi"
              value={item.osi}
              onChange={(event) => handleInputChange(event, index)}
              helperText="Only if the airline requires an OSI on bookings"
              slotProps={{ htmlInput: { maxLength: OSI_SSR_MAX_LENGTH } }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>SSR code:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField
              name="ssrCode"
              value={item.ssrCode}
              onChange={(event) => handleInputChange(event, index)}
              helperText="4 characters, e.g. OTHS. Only if the airline requires an SSR on bookings"
              slotProps={{ htmlInput: { maxLength: SSR_CODE_LENGTH } }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>SSR message:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <TextField
              name="ssrMessage"
              value={item.ssrMessage}
              onChange={(event) => handleInputChange(event, index)}
              helperText="e.g. CORP DEAL 12345. Only if the airline requires an SSR on bookings"
              slotProps={{ htmlInput: { maxLength: OSI_SSR_MAX_LENGTH } }}
            />
          </FormControl>
        </Grid>
      </Grid>
      <Grid alignItems="center" container flexDirection="row" size={12}>
        <Grid size={1}>
          <Typography>Membership Status:</Typography>
        </Grid>
        <Grid size={5}>
          <FormControl fullWidth>
            <Select
              label="verified"
              name="verified"
              notched={false}
              onChange={(event) => onSelectVerified(event, index)}
              value={item.verified}
            >
              <MenuItem disabled></MenuItem>
              <MenuItem value="true">Verified membership</MenuItem>
              <MenuItem value="false">No membership</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Grid>
  );
}
